// @story HIR-HD2
// PipelinePage — the Head's view of hiring: every open role, how many
// candidates sit at each stage, how long they have sat there, and the roles
// where nothing has moved. Read-only. Data: GET /api/dashboard/pipeline.
//
// Laid out as the other EquaOS apps are (owner, 29 Oct 2026: "the pipeline
// screen needs to be fixed again … with some blocks and then clickable"):
// a row of blocks, each a filter on the roles below it (All, Awaiting a
// decision, Moving, Stalled, No applicants yet), then the roles as one table.
// A row opens that role's shortlist. The stalled rule (how many days without
// movement) sits under the blocks, and the school's recruitment administrator
// can change it there.
//
// The shortlists live here too (owner, 29 Oct 2026: "the shortlist and the
// pipeline should come at one place"): each role shows who HR has put forward
// to the Head and how many still wait for a decision, from GET
// /api/shortlist-review, and "Awaiting a decision" lists the roles to decide.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { Button, Card, Icon, Input, StatusPill, Table } from '../../ds';
import { Page, PageHeader, StatRow, Stat, Section, Loading, Note } from '../../components/page';

const fmtDays = (d) => (d === null || d === undefined ? '—' : `${Math.round(d)} day${Math.round(d) === 1 ? '' : 's'}`);

const totalOf = (r) => r.stages.reduce((n, s) => n + s.count, 0);

/* The blocks, and which roles each one shows. */
const VIEWS = [
  { key: 'all', label: 'Open roles', hint: 'every vacancy still open', test: () => true },
  { key: 'awaiting', label: 'Awaiting a decision', hint: 'put forward to the Head', test: (r) => r.shortlist.awaiting > 0, tone: 'warning' },
  { key: 'moving', label: 'Moving', hint: 'candidates moved recently', test: (r) => !r.stalled && totalOf(r) > 0 },
  { key: 'stalled', label: 'Stalled', hint: 'candidates, but nothing has moved', test: (r) => r.stalled && totalOf(r) > 0, tone: 'danger' },
  { key: 'empty', label: 'No applicants yet', hint: 'nobody has applied', test: (r) => totalOf(r) === 0, tone: 'warning' },
];

export default function PipelinePage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [params, setParams] = useSearchParams();
  const view = params.get('view') || 'all';
  const setView = (key) => setParams(key === 'all' ? {} : { view: key }, { replace: true });
  // Who HR has put forward, by role (the shortlist list); null until loaded.
  const [shortlists, setShortlists] = useState(null);
  const [editing, setEditing] = useState(false);
  const [days, setDays] = useState('');
  const [saveMsg, setSaveMsg] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    // The shortlists are a second, optional read: without them the pipeline still shows.
    api.get('/shortlist-review')
      .then((r) => setShortlists(r.data))
      .catch(() => setShortlists({ roles: [], canDecide: false }));
    return api.get('/dashboard/pipeline')
      .then((r) => { setData(r.data); setDays(String(r.data.stalledAfterDays)); setErr(''); })
      .catch((e) => setErr(e?.response?.status === 403
        ? 'You do not have access to the hiring pipeline. Ask your administrator.'
        : 'Could not load the pipeline. If this persists, tell your developer.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveThreshold = () => {
    const n = Number(days);
    if (!Number.isInteger(n) || n < 1 || n > 90) { setSaveMsg('Enter a whole number of days, 1 to 90.'); return; }
    setSaveMsg('');
    api.put('/school-settings/pipeline', { stalledAfterDays: n })
      .then(() => { setEditing(false); load(); })
      .catch(() => setSaveMsg('Could not save — try again.'));
  };

  const NONE = { putForward: 0, awaiting: 0, approved: 0, onHold: 0, rejected: 0 };
  const tally = useMemo(() => new Map((shortlists?.roles || []).map((s) => [String(s.jobId), s])), [shortlists]);
  // The pipeline's own shortlist counts; from the shortlist list for a server without them.
  const roles = useMemo(() => (data ? data.roles : []).map((r) => ({ ...r, shortlist: r.shortlist || tally.get(String(r.jobId)) || NONE })), [data, tally]); // eslint-disable-line react-hooks/exhaustive-deps
  const viewLabel = (v) => (v.key === 'awaiting' && shortlists?.canDecide ? 'Awaiting your decision' : v.label);
  const counts = useMemo(() => Object.fromEntries(VIEWS.map((v) => [v.key, roles.filter(v.test).length])), [roles]);
  const current = VIEWS.find((v) => v.key === view) || VIEWS[0];
  const shown = roles.filter(current.test);

  const columns = [
    { key: 'title', label: 'Role', render: (r) => (
      <div>
        <div style={{ fontWeight: 500, color: 'var(--text-1)' }}>{r.title}</div>
        <small style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' }}>
          {r.openings} post{r.openings === 1 ? '' : 's'}
          {!r.published && ' · not on the careers page'}
        </small>
      </div>
    ) },
    { key: 'candidates', label: 'Candidates', render: (r) => <span className="num" style={{ fontWeight: 600 }}>{totalOf(r)}</span> },
    { key: 'shortlist', label: 'Shortlist', render: (r) => {
      const s = r.shortlist;
      if (!s.putForward) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
      return (
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span>{s.putForward} put forward</span>
          {s.awaiting > 0 && <StatusPill tone="warning">{s.awaiting} awaiting</StatusPill>}
          {s.approved > 0 && <StatusPill tone="success">{s.approved} approved</StatusPill>}
        </span>
      );
    } },
    { key: 'where', label: 'Where they are', render: (r) => {
      const at = r.stages.filter((s) => s.count > 0);
      if (!at.length) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
      return (
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {at.map((s) => <StatusPill key={s.stage} tone="neutral" dot={false}>{s.stage} {s.count}</StatusPill>)}
        </span>
      );
    } },
    { key: 'moved', label: 'Last movement', render: (r) => (r.daysSinceMovement === null ? '—' : `${fmtDays(r.daysSinceMovement)} ago`) },
    { key: 'status', label: 'Status', render: (r) => {
      if (r.filled) return <StatusPill tone="success">Filled</StatusPill>;
      if (r.stalled && totalOf(r)) return <StatusPill tone="danger">Stalled</StatusPill>;
      if (!totalOf(r)) return <StatusPill tone="warning">No applicants</StatusPill>;
      return <StatusPill tone="info">Moving</StatusPill>;
    } },
  ];

  return (
    <Page>
      <PageHeader
        title="Pipeline"
        subtitle="Open roles, where their candidates are, and how long they have been there."
        actions={<Button variant="secondary" icon={<Icon name="arrow-clockwise" spin={loading} />} onClick={load}>Refresh</Button>}
      />

      {err && <Note tone="warning">{err}</Note>}
      {loading && !data && <Card><Loading label="Loading the pipeline" /></Card>}

      {data && (
        <>
          <StatRow>
            {VIEWS.map((v) => (
              <Stat key={v.key} label={viewLabel(v)} value={counts[v.key]} hint={v.hint}
                tone={counts[v.key] ? v.tone : undefined}
                active={view === v.key} onClick={() => setView(v.key)} />
            ))}
          </StatRow>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>
            {editing ? (
              <>
                Stalled after
                <span style={{ width: 80 }}><Input type="number" min="1" max="90" value={days} onChange={(e) => setDays(e.target.value)} aria-label="Days without movement" /></span>
                days without movement
                <Button size="sm" onClick={saveThreshold}>Save</Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setDays(String(data.stalledAfterDays)); setSaveMsg(''); }}>Cancel</Button>
              </>
            ) : (
              <>
                A role is stalled when no candidate has moved in {data.stalledAfterDays} days.
                {data.canConfigure && <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>Change</Button>}
              </>
            )}
            {saveMsg && <span style={{ color: 'var(--status-danger-text)' }}>{saveMsg}</span>}
          </div>

          <Section title={viewLabel(current)} subtitle={`${shown.length} role${shown.length === 1 ? '' : 's'} · open one to see its shortlist`} padding="20px 0 8px">
            {shown.length === 0
              ? <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>No roles here.</div>
              : (
                <div style={{ overflowX: 'auto' }}>
                  <Table columns={columns} rows={shown} getRowId={(r) => r.jobId} onRowClick={(r) => navigate(`/shortlists/${r.jobId}`)} />
                </div>
              )}
          </Section>

          <p style={{ margin: 0, fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' }}>
            Wait is measured from the last thing that moved each candidate: applying, paying, an invite, a round finished,
            a panel decision, an offer step. Candidates who have joined or are not moving forward are not counted as waiting.
          </p>
        </>
      )}
    </Page>
  );
}
